import {
  Alarm20,
  OverflowMenuVertical16,
  Purchase16,
  RotateClockwise16,
  TrashCan16,
} from '@carbon/icons-react';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { components } from 'react-select';
import styled from 'styled-components';
import { Role, RoleType, User, UserStatus, UserType } from 'api/models';
import { ProjectAPI } from 'api/project';
import { Avatar } from 'components/atoms/avatar';
import {
  CpBody00Primary,
  CpBody01Primary,
} from 'components/atoms/customer-portal/typography';
import { Form } from 'components/atoms/form';
import { Loading } from 'components/atoms/loading';
import { CreatableSelect } from 'components/atoms/select/creatable';
import { MembersAPI, UpdateMemberPayload } from 'data/members/api';
import { InvitedMembers } from 'data/members/slice';
import { useGetProjectMembers, useProjectDetail } from 'data/projects';
import { useInAndNotInProjectUsers } from 'hooks/use-assignee-options';
import { useTranslation } from 'hooks/use-translation';
import { useRolesQuery } from 'queries/roles';
import { fullName } from 'utils/full-name';
import { isValidEmail } from 'utils/is-valid-email';
import { CpBlackButton } from '../cp-button';
import { CpMenu } from '../modal/cp-menu';
import { CpModal } from '../modal/cp-modal';
import { CpPopover } from '../modal/cp-popover';
import { CpSelect } from '../task-drawer/fields/select-components';

const InputAndButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  margin-top: var(--cp-spacing-05);
  margin-bottom: var(--cp-spacing-05);
  .ozone-select-container {
    width: 100%;
    margin-right: var(--cp-spacing-05);
  }
  ${CpBlackButton} {
    @media (max-width: 480px) {
      display: none;
    }
  }
`;

const ListOfMembersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: var(--cp-spacing-05);
  overflow-y: auto;

  @media (min-width: 480px) {
    max-height: 500px;
  }
`;

const noRoleId = -1;

const AssignRoleModal: React.FC<{
  visible?: boolean;
  onClose: () => void;
  onUpdate: (r: Role | undefined) => Promise<void>;
  defaultRoleId?: number;
}> = ({ visible, onClose, onUpdate, defaultRoleId }) => {
  const { data } = useRolesQuery();
  const [isLoading, setLoading] = useState(false);

  const noRoleText = useTranslation('project.members.no_role.label');
  const options = useMemo(
    () =>
      [
        {
          label: noRoleText,
          value: noRoleId,
        },
      ].concat(
        (data ?? [])
          .filter(({ type }) => type === RoleType.Customer)
          .map(({ roleName, roleId }) => ({
            label: roleName,
            value: roleId,
          })),
      ),
    [data, noRoleText],
  );
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(
    defaultRoleId ?? noRoleId,
  );

  const selectedRole = useMemo(() => {
    return options.find((o) => o.value === selectedRoleId);
  }, [options, selectedRoleId]);

  useEffect(() => {
    if (visible) {
      setSelectedRoleId(
        options.find(({ value }) => value === noRoleId)?.value ?? noRoleId,
      );
    }
  }, [visible, options]);

  return (
    <CpModal
      visible={visible}
      onCancel={onClose}
      title={<FormattedMessage id="project.members.add_role.label" />}
      okText={<FormattedMessage id="update" />}
      getContainer={() =>
        document.getElementById('cp-modal-root') as HTMLElement
      }
      onOk={() => {
        if (selectedRoleId !== null) {
          const r = data?.find((d) => d.roleId === selectedRoleId);

          setLoading(true);
          try {
            onUpdate?.(r)?.then(() => {
              setLoading(false);
            });
          } catch {
            setLoading(false);
          }
        }
      }}
      okButtonProps={{
        loading: isLoading,
      }}
    >
      <Form layout="vertical">
        <Form.Item label={<FormattedMessage id="role" />} name="role">
          <CpSelect
            options={options}
            onChange={(change) => setSelectedRoleId(change?.value)}
            value={selectedRole}
          />
        </Form.Item>
      </Form>
    </CpModal>
  );
};

const ResendInvitationConfirmationModal: React.FC<{
  visible?: boolean;
  onClose: () => void;
  onResend: () => Promise<void>;
  user?: User;
}> = ({ visible, onClose, onResend, user }) => {
  const [isLoading, setLoading] = useState(false);

  return (
    <CpModal
      visible={visible}
      onCancel={onClose}
      onOk={() => {
        setLoading(true);
        try {
          onResend?.().finally(() => {
            setLoading(false);
          });
        } catch {
          setLoading(false);
        }
      }}
      title={<FormattedMessage id="invitation.resend.confirm.title" />}
      okButtonProps={{
        loading: isLoading,
      }}
    >
      <CpBody01Primary>
        {user ? (
          <FormattedMessage
            id="invitation.resend.project.confirm.content"
            values={{ user: user.emailId }}
          />
        ) : null}
      </CpBody01Primary>
    </CpModal>
  );
};

const MemberWrapper = styled.div`
  &:hover {
    background: var(--cp-blue-10);
  }
  width: 100%;
  border-bottom: 1px solid var(--cp-border-subtle);
  display: flex;
  align-items: center;
  justify-content: start;
  padding: var(--cp-spacing-03);
`;

const AvatarAndNameWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  width: 100%;
`;

const MemberAvatar = styled(Avatar)`
  margin-right: var(--cp-spacing-05);
`;

const NameAndRoleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 80%;
`;

const MemberActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;

  svg {
    visibility: hidden;
  }

  ${MemberWrapper}:hover & {
    svg {
      visibility: visible;
    }
  }
`;

const IconWrapper = styled.div`
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-default);

  &:hover {
    background: var(--cp-gray-10);
  }
`;

const MemberName = styled(CpBody01Primary)``;
const MemberRole = styled(CpBody00Primary)``;

const Member: React.FC<{
  user: User;
  onRemove?: () => Promise<void>;
  onAddRole?: () => void;
  onResendInvitation?: () => void;
}> = ({ user, onRemove, onAddRole, onResendInvitation }) => {
  const [isRemoving, setRemoving] = useState(false);

  return (
    <MemberWrapper>
      <AvatarAndNameWrapper>
        <MemberAvatar src={user.photoUrl} name={fullName(user)} />
        <NameAndRoleWrapper>
          <MemberName>{fullName(user)}</MemberName>
          <MemberRole>{user.role?.roleName}</MemberRole>
        </NameAndRoleWrapper>
      </AvatarAndNameWrapper>
      <MemberActionsWrapper>
        {user?.userStatus === UserStatus.Invited ? (
          <IconWrapper>
            <Alarm20 color="var(--yellow-50)" />
          </IconWrapper>
        ) : null}
        {user?.userType !== UserType.External ? (
          <></>
        ) : (
          <CpPopover
            getPopupContainer={() =>
              document.getElementById('cp-modal-root') as HTMLDivElement
            }
            placement="bottomRight"
            overlay={
              <CpMenu>
                <CpMenu.Header>
                  <FormattedMessage id="more_actions" />
                </CpMenu.Header>
                {user?.userStatus === UserStatus.Invited ? (
                  <CpMenu.Item
                    icon={<RotateClockwise16 />}
                    onClick={onResendInvitation}
                  >
                    <FormattedMessage id="invitation.resend" />
                  </CpMenu.Item>
                ) : (
                  <></>
                )}
                <CpMenu.Item icon={<Purchase16 />} onClick={onAddRole}>
                  <FormattedMessage id="project.members.add_role.label" />
                </CpMenu.Item>
                <CpMenu.Divider />
                <CpMenu.ItemDelete
                  icon={
                    isRemoving ? (
                      <Loading size="small" />
                    ) : (
                      <TrashCan16 color="var(-red-60)" />
                    )
                  }
                  onClick={() => {
                    setRemoving(true);
                    try {
                      onRemove?.().finally(() => {
                        setRemoving(false);
                      });
                    } catch {
                      setRemoving(false);
                    }
                  }}
                >
                  <FormattedMessage id="remove" />
                </CpMenu.ItemDelete>
              </CpMenu>
            }
          >
            <IconWrapper>
              <OverflowMenuVertical16 />
            </IconWrapper>
          </CpPopover>
        )}
      </MemberActionsWrapper>
    </MemberWrapper>
  );
};

type Option = {
  label: string;
  value: string;
};

export const CpInviteToProjectModal: React.FC<{
  projectId: number;
  visible?: boolean;
  onClose?: () => void;
}> = ({ projectId, onClose, visible }) => {
  const { data: projectData } = useProjectDetail(['project_id', projectId]);
  const { data: projectMembersResponse, refetch: refetchProjectMembers } =
    useGetProjectMembers(projectId);

  const [selectedUsers, setSelectedUsers] = useState<Option[]>([]);
  const [newlyAddedUsers, setNewlyAddedUsers] = useState<string[]>([]);
  const [userInput, setUserInput] = useState<string>('');
  const [isInviting, setIsInviting] = useState(false);
  const [assignRoleModalUser, setAssignRoleModalUser] =
    useState<User | undefined>(undefined);
  const [resendInvitationModalUser, setResendInvitationModalUser] =
    useState<User | undefined>(undefined);

  const { notInProjectUsers } = useInAndNotInProjectUsers({
    projectId: ['project_id', projectId],
    options: {
      includeRestricedPartnersMembers: false,
    },
  });

  const options = useMemo(() => {
    return notInProjectUsers
      .map((user) => ({
        value: user.emailId,
        label: fullName(user),
      }))
      .concat(
        newlyAddedUsers.map((email) => ({
          label: email,
          value: email,
        })),
      );
  }, [newlyAddedUsers, notInProjectUsers]);

  const NoOptionsMessage = useCallback(
    (props) => {
      return (
        <components.NoOptionsMessage {...props}>
          {!userInput ? (
            <FormattedMessage id="input.email" />
          ) : isValidEmail(userInput) ? (
            <FormattedMessage id="invitation.create.placeholder" />
          ) : (
            <FormattedMessage id="input.email" />
          )}
        </components.NoOptionsMessage>
      );
    },
    [userInput],
  );

  const handleInputChange = useCallback(
    (input, action) => {
      if (action.action !== 'input-blur' && action.action !== 'menu-close') {
        if (input) {
          setUserInput(input.trim().toLowerCase());
          if (input.includes(',')) {
            const emails = input.split(',');

            setSelectedUsers([
              ...selectedUsers,
              ...emails
                .filter((e) => isValidEmail(e))
                .map((e) => ({
                  label: e,
                  value: e,
                })),
            ]);

            setUserInput('');
          }
        } else {
          setUserInput('');
        }
      }
    },
    [selectedUsers],
  );

  const onInvite = useCallback(
    async (emails: string[]) => {
      if (!projectData?.customer) {
        return;
      }
      await ProjectAPI.createAndInviteProjectMembers(
        projectId,
        emails.map((e) => ({
          user: {
            emailId: e,
            company: projectData?.customer,
          },
        })),
      );
      await refetchProjectMembers();
    },
    [projectData?.customer, projectId, refetchProjectMembers],
  );

  const onInviteClick = useCallback(() => {
    setIsInviting(true);
    onInvite?.(selectedUsers.map((u) => u.value))
      .finally(() => {
        setIsInviting(false);
      })
      ?.then(() => {
        setSelectedUsers([]);
        setNewlyAddedUsers([]);
        setUserInput('');
        setIsInviting(false);
      });
  }, [onInvite, selectedUsers]);

  return (
    <CpModal
      visible={visible}
      onCancel={onClose}
      onOk={onInviteClick}
      okButtonProps={{
        loading: isInviting,
      }}
      title={
        projectData?.projectName ? (
          <FormattedMessage
            id="cp.invite_modal.title"
            values={{
              projectName: projectData.projectName,
            }}
          />
        ) : undefined
      }
      getContainer={() =>
        document.getElementById('cp-modal-root') as HTMLElement
      }
    >
      <InputAndButtonWrapper>
        <CreatableSelect
          type="user"
          options={options}
          onChange={(change: Option[]) => {
            setSelectedUsers(change);
          }}
          isValidNewOption={isValidEmail}
          formatCreateLabel={(input) => {
            if (isValidEmail(input)) {
              return <FormattedMessage id="invitation.create.placeholder" />;
            }

            return <FormattedMessage id="input.email" />;
          }}
          onInputChange={handleInputChange}
          onCreateOption={(inputValue) => {
            setNewlyAddedUsers([...newlyAddedUsers, inputValue]);
            setSelectedUsers([
              ...selectedUsers,
              {
                label: inputValue,
                value: inputValue,
              },
            ]);
          }}
          value={selectedUsers}
          isMulti
          placeholder="john@example.com"
          components={{
            DropdownIndicator: () => <></>,
            NoOptionsMessage,
          }}
        />
        <CpBlackButton loading={isInviting} onClick={onInviteClick}>
          <FormattedMessage id="invite" />
        </CpBlackButton>
      </InputAndButtonWrapper>
      <CpBody01Primary>
        <FormattedMessage id="cp.invite_modal.part_of_project" />
      </CpBody01Primary>
      <ListOfMembersWrapper>
        {projectMembersResponse?.userList?.map((user) => (
          <Member
            key={user?.userId}
            user={user}
            onRemove={async () => {
              ProjectAPI.removeMembers(projectId, [user]).then(() => {
                refetchProjectMembers();
              });
            }}
            onAddRole={() => {
              setAssignRoleModalUser(user);
            }}
            onResendInvitation={() => {
              setResendInvitationModalUser(user);
            }}
          />
        ))}
      </ListOfMembersWrapper>
      <AssignRoleModal
        onClose={() => setAssignRoleModalUser(undefined)}
        onUpdate={async (role) => {
          if (assignRoleModalUser) {
            MembersAPI.updateMember({
              user: {
                ...(assignRoleModalUser ?? {}),
                userId: assignRoleModalUser?.userId,
                role:
                  role?.roleId && role?.roleId !== noRoleId ? role : undefined,
              },
            } as unknown as UpdateMemberPayload);
          }
        }}
        visible={!!assignRoleModalUser}
        defaultRoleId={assignRoleModalUser?.role?.roleId}
      />
      <ResendInvitationConfirmationModal
        visible={!!resendInvitationModalUser}
        onClose={() => setResendInvitationModalUser(undefined)}
        user={resendInvitationModalUser}
        onResend={async () => {
          if (resendInvitationModalUser) {
            ProjectAPI.addMembers(projectId, [
              { user: { userId: resendInvitationModalUser?.userId } },
            ] as unknown as InvitedMembers[]).then(() => {
              refetchProjectMembers();
            });
          }
          setResendInvitationModalUser(undefined);
        }}
      />
    </CpModal>
  );
};
